import { Eye } from "lucide-react";
import PageHero, { usePageTitle } from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import CTABand from "@/components/CTABand";
import { PILLARS, VALUES, CONTACT } from "@/data/content";

const MountainFlag = ({ className = "", strokeWidth = 1.5 }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M2 20 L9 9 L12.5 14.5 L16 10.5 L22 20" />
    <path d="M2 20 H22" />
    <path d="M9 9 V3.2" />
    <path d="M9 3.2 H14.2 L12.9 4.8 L14.2 6.4 H9" />
  </svg>
);

export default function Nosotros() {
  usePageTitle("Nosotros | BRIK Procurement Partners");
  return (
    <>
      <PageHero
        eyebrow="Nosotros"
        titleLines={["Más que compras,", "construimos oportunidades."]}
        description="BRIK Procurement Partners es una firma especializada en transformar la función de Compras en una ventaja competitiva sostenible."
        image="/brand/ref-office-port.webp"
        alt="Oficina ejecutiva BRIK con vista a operaciones de comercio global"
      />

      <section data-testid="about-intro" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-12 gap-14 items-start">
          <div className="lg:col-span-6">
            <SectionHeader eyebrow="Quiénes somos" title="Somos tu aliado estratégico en Procurement." />
            <Reveal delay={0.2}>
              <p className="mt-8 text-base leading-relaxed text-carbon/80">
                BRIK Procurement Partners es una firma mexicana especializada en transformar y profesionalizar la función de Compras.
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <p className="mt-4 text-base leading-relaxed text-carbon/70">
                Acompañamos a empresas en crecimiento a estructurar, fortalecer y operar sus procesos de Procurement, combinando estrategia, experiencia e inteligencia de mercado para obtener mejores condiciones, desarrollar proveedores y tomar mejores decisiones.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <blockquote className="mt-10 border-l-2 border-gold pl-6 py-1">
                <p className="font-display text-xl lg:text-2xl font-semibold text-oxford leading-snug">
                  Construimos capacidades de Compras que permanecen y generan valor.
                </p>
              </blockquote>
            </Reveal>
          </div>
          <div className="lg:col-span-6 relative">
            <Reveal delay={0.2}>
              <span className="absolute -top-4 -right-4 w-full h-full border border-gold/40 pointer-events-none" />
              <img src="/brand/brand-meeting.webp" alt="Equipo BRIK en sesión de trabajo estratégico de Procurement" loading="lazy" className="relative w-full aspect-[4/3] object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="purpose-band" className="relative bg-oxford diagonal-lines overflow-hidden">
        <img src="/brand/isotipo-gold.webp" alt="" className="absolute -right-20 -bottom-24 w-96 opacity-[0.05] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-24">
          <Reveal>
            <span className="eyebrow">Nuestro propósito</span>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-3xl font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-white leading-snug">
              Profesionalizar la función de Compras y convertirla en una <span className="text-gold">fuente sostenible de valor</span> para tu empresa.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="pillars-section" className="bg-bone">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionHeader
            eyebrow="Cómo lo hacemos"
            title="Construimos capacidades de Procurement que convierten Compras en una ventaja competitiva."
            description="Integramos estrategia, procesos, proveedores, negociación, inteligencia y ejecución para generar resultados que permanezcan en el negocio."
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-oxford/10 border border-oxford/10">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08} className="h-full">
                <div className="group relative h-full bg-white p-8 flex flex-col items-center text-center transition-all duration-300 hover:-translate-y-1 hover:bg-bone">
                  <span className="absolute top-0 left-0 h-[2px] w-0 bg-gold transition-all duration-500 group-hover:w-full" />
                  <span className="font-display text-sm font-semibold text-gold/70">0{i + 1}</span>
                  <p.icon className="mt-5 h-8 w-8 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-4 font-display text-sm font-semibold tracking-[0.2em] uppercase text-oxford">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-carbon/70">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="mission-section" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid lg:grid-cols-3 gap-12">
          <Reveal>
            <div className="border-t-2 border-gold pt-8">
              <MountainFlag className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <h3 className="mt-5 font-display text-lg font-semibold uppercase tracking-wide text-oxford">Misión</h3>
              <p className="mt-4 text-sm leading-relaxed text-carbon/70">
                Transformar y profesionalizar la función de Compras de nuestros clientes, construyendo capacidades de Procurement que generen valor, mejores decisiones y ventajas competitivas sostenibles.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border-t-2 border-gold pt-8">
              <Eye className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <h3 className="mt-5 font-display text-lg font-semibold uppercase tracking-wide text-oxford">Visión</h3>
              <p className="mt-4 text-sm leading-relaxed text-carbon/70">
                Ser una firma mexicana referente en Procurement, reconocida por transformar la manera en que las empresas compran, desarrollan proveedores y generan valor.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="border-t-2 border-gold pt-8">
              <h3 className="font-display text-lg font-semibold uppercase tracking-wide text-oxford">Nuestros valores</h3>
              <ul className="mt-4 space-y-3">
                {VALUES.map((v) => (
                  <li key={v.title} className="flex items-center gap-3 text-sm text-carbon/80">
                    <v.icon className="h-4 w-4 text-gold shrink-0" strokeWidth={1.5} /> {v.title}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="leadership-section" className="bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32">
          <SectionHeader eyebrow="Liderazgo" title="Experiencia ejecutiva en cada proyecto." />
          <Reveal delay={0.15}>
            <div className="mt-14 grid lg:grid-cols-12 border border-oxford/10 bg-bone overflow-hidden">
              <div className="lg:col-span-6 relative">
                <img
                  src="/brand/brand-valeria.webp"
                  alt="Valeria Longinos — Founder & Managing Director de BRIK Procurement Partners"
                  loading="lazy"
                  className="w-full h-full min-h-[320px] lg:min-h-[440px] object-cover object-center"
                />
                <span className="absolute top-0 right-0 h-full w-[2px] bg-gold hidden lg:block" />
              </div>
              <div className="lg:col-span-6 p-10 lg:p-14 flex flex-col justify-center">
                <h3 className="font-display text-2xl font-semibold text-oxford">{CONTACT.founder}</h3>
                <p className="mt-2 font-display text-[11px] font-semibold tracking-[0.22em] uppercase text-gold">{CONTACT.role}</p>
                <span className="mt-6 h-px w-16 bg-gold" />
                <blockquote className="mt-6 space-y-4 text-sm lg:text-base leading-relaxed text-carbon/70 italic text-justify">
                  <p>
                    “BRIK nace de algo en lo que creo profundamente: detrás de una buena compra siempre hay personas, relaciones, conocimiento y decisiones bien tomadas.
                  </p>
                  <p>
                    Quiero que cada empresa que trabaje con nosotros encuentre más que un consultor: un equipo que entienda su negocio, se involucre y construya a su lado, compartiendo experiencia, conocimiento y una red que le permita crecer.
                  </p>
                  <p>
                    Porque cuando construimos mejores Compras, también construimos mejores negocios.”
                  </p>
                </blockquote>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </>
  );
}
